import { Tooltip } from '@mui/material'

import { useDatasets } from 'api/dataset/dataset.api'
import {
  useCopyBidsFromDate,
  useElplanBidCreateMutation,
  useElplanInfo,
  useElplanSendingBidMutation,
  useSentBidsToBroker,
} from 'api/elplan/elplan.api'
import { resolveElplanBidsQueryEndTime } from 'api/elplan/elplan.bidQueryWindow.helper'
import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import { BidTypeName } from 'api/elplan/elplan.types'
import { postStat } from 'api/stats/stats.api'
import { useUiConfigAnchorComponent } from 'api/uiConfig/uiConfig.api'
import { getCalculatedDataset } from 'helpers/dataset.helper/dataset.calc.helper'
import { exportPlannedElectricityProductionToExcel } from 'helpers/elplan.helper/elplanExport.helper'
import { convertNumberDecimalSign, isArray } from 'helpers/global.helper/global.helper'
import i18n from 'i18next'
import React, { ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore from 'store/elplan/elplan'
import { colors } from 'styles/variables'
import { Button, Card } from 'ui/atoms'
import LineDivider from 'ui/atoms/Line/LineDivider'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { BidSubmitConfirmDialog } from 'ui/components/BidSubmitConfirmDialog/BidSubmitConfirmDialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import UiConfig from 'ui/uiConfig/UiConfig'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import { DatepickerInFormWithSwitch } from '../shared/DatepickerInFormWithSwitch'
import BidModal from '../shared/DayAheadBidEditor/BidModal/BidModal'
import CopyBidsModal from '../shared/DayAheadBidEditor/CopyBidsModal/CopyBidsModal'
import { DayAheadBidEditor } from '../shared/DayAheadBidEditor/DayAheadBidEditor'
import { useDayAheadBidTagSelectionContext } from '../shared/DayAheadBidEditor/useDayAheadBidTagSelectionContext'
import {
  FormattedBid,
  FormattedBidWithIdAndTitle,
  getDatasetInstructionsForSumVolumeCalc,
  getItemsForExcel,
} from './DayAheadBidManagement.helper'
import styles from './DayAheadBidManagement.module.less'

const showSentBidsAtBanner = (sentBidsToBrokerAtDate: undefined | { latest_date: ISODateTime }): ReactElement => {
  if (sentBidsToBrokerAtDate?.latest_date) {
    const formattedDate = Datetime.toLocalTime(sentBidsToBrokerAtDate.latest_date, 'hour')
    return (
      <InfoBanner
        style={'info'}
        variant="text"
        text={i18n.t(`Bids sent at {{sentDate}}.`, {
          sentDate: formattedDate,
        })}
      />
    )
  }

  return <InfoBanner style={'warning'} variant="text" text={i18n.t('No bids sent')} />
}

export default function DayAheadBidManagement(): ReactElement {
  const elplanSnap = useSnapshot(elplanStore)
  const { t } = useTranslation()
  const auth = useAuth()
  const activeSystem = auth.activeSystem
  const isSuperUser = auth.user?.is_superuser === true
  const [isBidConfirmOpen, setIsBidConfirmOpen] = useState(false)

  const selectedStartTime = elplanSnap.date.start_time ?? Datetime.getTomorrowDate().startTime
  const selectedEndTime = elplanSnap.date.end_time ?? Datetime.getTomorrowDate().endTime
  const selectedDate = {
    startTime: selectedStartTime,
    endTime: selectedEndTime,
  }

  const [typeOfBidToCopy, setTypeOfBidToCopy] = useState<BidTypeName>()
  const [currentBlockBid, setCurrentBlockBid] = useState<FormattedBidWithIdAndTitle>()

  const [currentProfileBid, setCurrentProfileBid] = useState<FormattedBidWithIdAndTitle>()

  const systemHasBlockBids = elplanSnap.bid_types.some((bid) => bid.type === 'blockbid')
  const systemHasProfileBids = elplanSnap.bid_types.some((bid) => bid.type === 'profilebid')
  const amountOfBlockBids = elplanSnap.blockBidInfo.amount || 0
  const amountOfProfileBids = elplanSnap.profileBidInfo.amount || 0

  // Datasets for excel export of bids
  const onlyReturnIds = useMemo(() => {
    const onlyReturnIds = ['spotbid_without_price.volume']

    if (systemHasBlockBids) {
      for (let i = 0; i < amountOfBlockBids; i++) {
        onlyReturnIds.push(`blockbid.${i + 1}.volume`)
        onlyReturnIds.push(`blockbid.${i + 1}.price`)
      }
    }

    if (systemHasProfileBids) {
      for (let i = 0; i < amountOfProfileBids; i++) {
        onlyReturnIds.push(`profilebid.${i + 1}.volume`)
        onlyReturnIds.push(`profilebid.${i + 1}.price`)
      }
    }

    return onlyReturnIds
  }, [amountOfBlockBids, amountOfProfileBids, systemHasBlockBids, systemHasProfileBids])

  const { data: elplanBidsUiConfig } = useUiConfigAnchorComponent('elplan_bids')
  const { data: elplanBidsDatasets = [] } = useDatasets(
    elplanBidsUiConfig?.id ?? -1,
    elplanBidsUiConfig?.version ?? -1,
    elplanBidsUiConfig?.dataset_instructions || [],
    selectedStartTime,
    resolveElplanBidsQueryEndTime(selectedEndTime),
    {
      onlyReturnIds,
      fillMissingHourTimestamps: false,
      ensureUniformTimeIntervals: false,
      datasetRefreshToken: ELPLAN_BID_REFRESH_EVENT_QUERY_KEY,
      overrideAlias: {
        elplan_info_id: elplanSnap.elplan_info_id,
      },
    }
  )

  const itemsForExcel = getItemsForExcel(amountOfBlockBids)
  const itemsDataId: Set<string> = new Set(itemsForExcel.map((item) => item.data_id))
  const visibleDatasets = elplanBidsDatasets.filter(({ return_id }) => itemsDataId.has(return_id))
  const datasetNames = (itemsForExcel || []).reduce((acc, item) => ({ ...acc, [item.data_id]: item.title }), {})

  const { mutate: createBid } = useElplanBidCreateMutation()
  const { mutate: copyBidsFromDate } = useCopyBidsFromDate()
  const { mutate: sendBidToElplan } = useElplanSendingBidMutation()

  function getBidVolumeWithConvertedDecimalSign(bid: FormattedBid): number | number[] | undefined {
    if (isArray(bid.volume)) {
      return bid.volume.map((vol) => convertNumberDecimalSign(vol))
    }
    if (typeof bid.volume === 'number') {
      return convertNumberDecimalSign(bid.volume)
    }
    return bid.volume
  }

  function addNewBlockOrProfileBid(bid: FormattedBid): void {
    createBid({
      elplan_info: elplanSnap.elplan_info_id,
      tag: bid.tag,
      start_time: Datetime.toISOString(bid.startTime),
      end_time: Datetime.toISOString(bid.endTime),
      type: bid.type,
      volume: getBidVolumeWithConvertedDecimalSign(bid),
      price: bid.price ? convertNumberDecimalSign(bid.price) : undefined,
      name: bid.name || '-',
      electricity_price_scenario: bid.scenarioId,
      generate_block_order: true,
    })
  }

  function copySpotBidsFromDate(date: ISODateTime, type: string): void {
    postStat('copy-spotbids-from-date', `click`)

    setTypeOfBidToCopy(undefined)
    copyBidsFromDate({
      elplan_info: elplanSnap.elplan_info_id,
      from_date: Datetime.toLocalTime(date, 'onlyDate') as ISODateTime,
      to_date: Datetime.toLocalTime(selectedStartTime, 'onlyDate') as ISODateTime,
      type: type,
    })
  }

  const sentBidsToBrokerAtDate = useSentBidsToBroker(
    elplanSnap.elplan_info_id,
    selectedStartTime,
    selectedEndTime,
    undefined
  )
  const { data: elplanInfo } = useElplanInfo(auth.systemId)
  const integrationExists = elplanInfo?.integration ?? false
  const { tagsForDate, requireTagForBlockBids, blockBidInputCurrency, profileBidInputCurrency } =
    useDayAheadBidTagSelectionContext()

  const calcDatasets = getCalculatedDataset(
    elplanBidsDatasets,
    getDatasetInstructionsForSumVolumeCalc(amountOfBlockBids)
  )
  const maxBidVolume = elplanSnap?.maxBidVolume
  const bidsIsOvermaxBidVolume =
    typeof maxBidVolume === 'number' ? calcDatasets.values.some((num) => num && num > maxBidVolume) : false

  function closeBlockBidModal(): void {
    setCurrentBlockBid(undefined)
  }

  function closeProfileBidModal(): void {
    setCurrentProfileBid(undefined)
  }

  return (
    <>
      <Card title={t(`Day ahead bid management`)}>
        <div className={styles.DayAheadBidManagement_Period}>
          <DatepickerInFormWithSwitch defaultDay={'tomorrow'} />
          <div className={styles.DayAheadBidManagement_Period__right}>
            {integrationExists && (
              <Tooltip
                title={
                  bidsIsOvermaxBidVolume
                    ? t(`Some bids are over the maximum bid volume. Max volume for bids is {{maxBidVolume}} MW`, {
                        maxBidVolume: elplanSnap.maxBidVolume,
                      })
                    : ''
                }
                arrow
              >
                <div className={styles.DayAheadBidManagement_SendBidsButtonContainer}>
                  <Button
                    primary
                    marginLeft
                    disabled={bidsIsOvermaxBidVolume}
                    onClick={() => {
                      if (!activeSystem) {
                        return
                      }
                      if (isSuperUser) {
                        setIsBidConfirmOpen(true)
                      } else {
                        sendBidToElplan({
                          date: Datetime.toLocalTime(selectedStartTime, 'onlyDate'),
                          system: activeSystem.name,
                          systemId: activeSystem.id,
                        })
                      }
                    }}
                  >
                    {t('Send bids')}
                  </Button>
                  {showSentBidsAtBanner(sentBidsToBrokerAtDate.data ?? undefined)}
                </div>
              </Tooltip>
            )}
            <TooltipIconButton
              icon="fas fa-cloud-download-alt"
              iconColor={colors.primary5}
              iconSize={'medium'}
              onClick={() => exportPlannedElectricityProductionToExcel(visibleDatasets, datasetNames, selectedDate)}
              tooltip={t('Export to Excel with 15 minute resolution')}
            />
          </div>
        </div>
        <UiConfig
          type="day_ahead_bid_management"
          datasetStartTime={selectedStartTime}
          datasetEndTime={selectedEndTime}
          datasetRefreshToken={ELPLAN_BID_REFRESH_EVENT_QUERY_KEY}
        />

        <LineDivider />

        <DayAheadBidEditor
          datasetStartTime={selectedStartTime}
          datasetEndTime={selectedEndTime}
          tagsForDate={tagsForDate}
          bidInputCurrencies={{
            block: blockBidInputCurrency,
            profile: profileBidInputCurrency,
          }}
          requireTagForBlockBids={requireTagForBlockBids}
          setCurrentBlockBid={setCurrentBlockBid}
          setCurrentProfileBid={setCurrentProfileBid}
          setTypeOfBidToCopy={setTypeOfBidToCopy}
        />
      </Card>
      {typeOfBidToCopy !== undefined && (
        <CopyBidsModal
          onSubmit={(chosenDate) => copySpotBidsFromDate(chosenDate, typeOfBidToCopy ?? '')}
          closeModal={() => setTypeOfBidToCopy(undefined)}
        />
      )}
      {currentBlockBid !== undefined && (
        <BidModal
          closeModal={closeBlockBidModal}
          bid={currentBlockBid}
          createBid={addNewBlockOrProfileBid}
          editBid={addNewBlockOrProfileBid}
          type={'block'}
          isEditing={currentBlockBid.fake}
          tagsForDate={tagsForDate}
          requireTagForBlockBids={requireTagForBlockBids}
          bidInputCurrency={blockBidInputCurrency}
        />
      )}
      {currentProfileBid !== undefined && (
        <BidModal
          closeModal={closeProfileBidModal}
          bid={currentProfileBid}
          createBid={addNewBlockOrProfileBid}
          editBid={addNewBlockOrProfileBid}
          type={'profile'}
          isEditing={currentProfileBid.fake}
          tagsForDate={tagsForDate}
          requireTagForBlockBids={requireTagForBlockBids}
          bidInputCurrency={profileBidInputCurrency}
        />
      )}
      {activeSystem && (
        <BidSubmitConfirmDialog
          open={isBidConfirmOpen}
          onClose={() => setIsBidConfirmOpen(false)}
          onConfirm={() => {
            setIsBidConfirmOpen(false)
            sendBidToElplan({
              date: Datetime.toLocalTime(selectedStartTime, 'onlyDate'),
              system: activeSystem.name,
              systemId: activeSystem.id,
            })
          }}
          systemName={activeSystem.name}
          isPreview={activeSystem.is_preview}
        />
      )}
    </>
  )
}
